/**
 * Lesson catalog — Sister Signal v1.1.
 *
 * Shape per lesson:
 *   slug, category, read_minutes,
 *   title_en / title_es,
 *   sections_en / sections_es — array of { heading?, body, list? }
 *   reflection_en / reflection_es,
 *   related — array of related slugs
 *
 * Each lesson renders through <LessonDetail/> via getLesson(slug).
 *
 * Spanish drafts are neutral Latin American (es-MX leaning), warm, trauma-
 * informed, "tú". Marked `// review pending` until the human Spanish editor
 * does a tone pass before public launch.
 */

const LESSONS = [
  // ---------------------------- BOUNDARIES ---------------------------- //
  {
    slug: "boundaries-101",
    category: "boundaries",
    read_minutes: 4,
    title_en: "Boundaries 101",
    title_es: "Límites 101",
    sections_en: [
      {
        heading: "What boundaries actually are",
        body: "A boundary is not a rule you impose on another person. It is a decision you make about what you will and will not do, regardless of how they respond. ‘Please stop yelling at me’ is a request. ‘I’ll leave the room when voices rise’ is a boundary — because you control it.",
      },
      {
        heading: "Why guilt shows up",
        body: "If you were raised to keep peace, your nervous system may have learned that other people’s comfort is your job. Saying no fires that old alarm. Guilt is not proof you’re doing something wrong — often it’s proof you’re doing something new.",
      },
      {
        heading: "Boundaries vs. punishment",
        body: "Boundaries are about your own safety. Punishment is about making them feel a consequence. The difference: a boundary holds even when nobody’s looking. Punishment requires an audience.",
      },
      {
        heading: "Three small scripts",
        list: [
          "‘That doesn’t work for me.’",
          "‘I’m not available for that conversation right now.’",
          "‘I’ll think about it and let you know tomorrow.’",
        ],
      },
    ],
    sections_es: [
      {
        heading: "Qué son los límites en realidad",
        body: "Un límite no es una regla que le impones a otra persona. Es una decisión que tomas sobre lo que tú harás o no harás, sin importar cómo responda esa persona. ‘Por favor deja de gritarme’ es una petición. ‘Me salgo del cuarto si suben las voces’ es un límite — porque tú lo controlas.",
      },
      {
        heading: "Por qué aparece la culpa",
        body: "Si te enseñaron a mantener la paz, tu sistema nervioso pudo aprender que la comodidad de los demás es tu responsabilidad. Decir no enciende esa alarma vieja. La culpa no es prueba de que estás haciendo algo mal — muchas veces es prueba de que estás haciendo algo nuevo.",
      },
      {
        heading: "Límites vs. castigo",
        body: "Los límites son sobre tu propia seguridad. El castigo es sobre hacerle sentir consecuencias a la otra persona. La diferencia: un límite se sostiene aunque nadie esté mirando. El castigo necesita público.",
      },
      {
        heading: "Tres frases pequeñas",
        list: [
          "‘Eso no me funciona.’",
          "‘No estoy disponible para esa conversación ahora.’",
          "‘Lo voy a pensar y te aviso mañana.’",
        ],
      },
    ],
    reflection_en: "Where do I most often say yes when I mean no?",
    reflection_es: "¿Dónde digo sí con más frecuencia cuando en realidad quiero decir no?",
    related: ["learning-to-say-no", "boundary-guilt"],
  },
  {
    slug: "learning-to-say-no",
    category: "boundaries",
    read_minutes: 3,
    title_en: "Learning to Say No",
    title_es: "Aprender a Decir No",
    sections_en: [
      {
        heading: "Why women over-explain",
        body: "Many of us learned that a refusal had to be justified into the ground or it wouldn’t hold. So we tack on reasons, apologies, and softeners — and the no quietly disappears under the explanation. Over-explaining isn’t kindness. It’s a leftover survival strategy.",
      },
      {
        heading: "No is a complete sentence",
        body: "You can be soft and still be clear. ‘No, thank you.’ ‘I can’t.’ ‘That doesn’t work for me.’ None of these need a paragraph after them. A safe person hears the no the first time. An unsafe one will not be convinced by your tenth explanation either.",
      },
      {
        heading: "Practice",
        list: [
          "For 24 hours, end one refusal at the period. Don’t add ‘because.’",
          "Notice the urge to soften with ‘sorry’ or ‘maybe next time.’ Pause.",
          "If you slip and over-explain, forgive yourself out loud.",
        ],
      },
    ],
    sections_es: [
      {
        heading: "Por qué muchas mujeres sobreexplican",
        body: "A muchas nos enseñaron que una negativa debía justificarse hasta la médula o no se sostenía. Por eso agregamos razones, disculpas y suavizantes — y el no desaparece debajo de la explicación. Sobreexplicar no es amabilidad. Es una estrategia vieja de supervivencia.",
      },
      {
        heading: "No es una oración completa",
        body: "Puedes ser suave y al mismo tiempo clara. ‘No, gracias.’ ‘No puedo.’ ‘Eso no me funciona.’ Ninguna necesita un párrafo después. Una persona segura te escucha la primera vez. Una insegura tampoco se va a convencer con tu décima explicación.",
      },
      {
        heading: "Práctica",
        list: [
          "Por 24 horas, termina una negativa en el punto. No agregues ‘porque’.",
          "Nota las ganas de suavizar con ‘perdón’ o ‘tal vez después’. Haz pausa.",
          "Si se te escapa y sobreexplicas, perdónate en voz alta.",
        ],
      },
    ],
    reflection_en: "What would become easier if I trusted my no?",
    reflection_es: "¿Qué se volvería más fácil si confiara en mi no?",
    related: ["boundaries-101", "boundary-guilt"],
  },
  {
    slug: "boundary-guilt",
    category: "boundaries",
    read_minutes: 2,
    title_en: "Boundary Guilt Is Not Boundary Failure",
    title_es: "La Culpa por un Límite No Es un Límite Fallido",
    sections_en: [
      {
        body: "When you set a new boundary, your body may flood with guilt, doubt, or the urge to apologize. This is not a sign you did something wrong. It’s a sign your nervous system is rewiring an old pattern in real time.",
      },
      {
        body: "Guilt and growth often arrive together. The presence of guilt does not invalidate the boundary. It just means you were trained to feel responsible for other people’s discomfort.",
      },
      {
        body: "Stay with the discomfort for ten breaths. Don’t take the boundary back to soothe yourself. The guilt softens. The boundary stays.",
      },
    ],
    sections_es: [
      {
        body: "Cuando pones un límite nuevo, tu cuerpo puede llenarse de culpa, duda o ganas de pedir perdón. Esto no es señal de que hiciste algo mal. Es señal de que tu sistema nervioso está reconfigurando un patrón viejo en tiempo real.",
      },
      {
        body: "La culpa y el crecimiento muchas veces llegan juntas. Que aparezca culpa no invalida el límite. Solo quiere decir que te enseñaron a sentirte responsable de la incomodidad de los demás.",
      },
      {
        body: "Quédate con la incomodidad por diez respiraciones. No deshagas el límite solo para calmarte. La culpa se suaviza. El límite se queda.",
      },
    ],
    reflection_en: "What feeling appears when I imagine disappointing someone?",
    reflection_es: "¿Qué sensación aparece cuando me imagino decepcionando a alguien?",
    related: ["boundaries-101", "learning-to-say-no"],
  },

  // -------------------------- COMMUNICATION -------------------------- //
  {
    slug: "gray-rock",
    category: "communication",
    read_minutes: 4,
    title_en: "Gray Rock",
    title_es: "Roca Gris",
    sections_en: [
      {
        heading: "When to use it",
        body: "Gray Rock is a strategy for limiting fuel to someone who escalates when they get any reaction — positive or negative. Useful with: high-conflict co-parents, narcissistic ex-partners, draining family members you can’t fully avoid, and anyone who uses your emotion against you.",
      },
      {
        heading: "When NOT to use it",
        body: "Don’t Gray Rock people you love and trust. It’s not a way to ‘win’ at intimacy. It’s a shield for unsafe dynamics — not a personality default. Used on safe people, it slowly erodes connection.",
      },
      {
        heading: "Neutral responses to practice",
        list: [
          "‘Okay.’",
          "‘That’s interesting.’",
          "‘I see.’",
          "‘I’ll think about it.’",
          "‘That’s a question for the lawyer / mediator / school.’",
        ],
      },
      {
        heading: "Protecting your energy",
        body: "Gray Rock works because high-conflict people are hunting for emotional fuel. When the well runs dry, they often move on to easier targets. Your job is not to teach them. Your job is to bore them.",
      },
    ],
    sections_es: [
      {
        heading: "Cuándo usarlo",
        body: "La Roca Gris es una estrategia para limitar el combustible a alguien que escala cuando obtiene cualquier reacción — buena o mala. Útil con: co-padres de alto conflicto, ex narcisistas, familiares que agotan y a quienes no puedes evitar del todo, y cualquiera que use tu emoción en tu contra.",
      },
      {
        heading: "Cuándo NO usarlo",
        body: "No le hagas Roca Gris a la gente que amas y en quien confías. No es una forma de ‘ganar’ en la intimidad. Es un escudo para dinámicas inseguras — no un modo por defecto. Aplicado en personas seguras, erosiona el vínculo poco a poco.",
      },
      {
        heading: "Respuestas neutras para practicar",
        list: [
          "‘Está bien.’",
          "‘Qué interesante.’",
          "‘Entiendo.’",
          "‘Lo voy a pensar.’",
          "‘Esa es pregunta para el abogado / mediador / la escuela.’",
        ],
      },
      {
        heading: "Cuida tu energía",
        body: "La Roca Gris funciona porque las personas de alto conflicto buscan combustible emocional. Cuando se seca el pozo, suelen moverse a blancos más fáciles. Tu trabajo no es enseñarles. Tu trabajo es aburrirles.",
      },
    ],
    reflection_en: "What conversations currently drain me?",
    reflection_es: "¿Qué conversaciones me están agotando ahora mismo?",
    related: ["boundaries-101"],
  },

  // ------------------------- FINANCIAL FREEDOM ------------------------- //
  {
    slug: "financial-readiness-basics",
    category: "financial-freedom",
    read_minutes: 3,
    title_en: "Financial Readiness Basics",
    title_es: "Bases de Preparación Económica",
    sections_en: [
      {
        body: "Financial readiness is not about being ready to leave. It is about giving yourself options. Every small step you take is one more door that quietly opens.",
      },
      {
        heading: "The ten-item readiness checklist",
        body: "Open the Financial Freedom card from the Sanctuary hub and tap each item as you complete it. Nothing is graded. Nothing is timed. Progress is information, not pressure.",
      },
      {
        heading: "How to use the checklist",
        list: [
          "Do one item this week. Just one.",
          "Save copies somewhere outside the home — a friend’s drawer, a cloud account he doesn’t know about, a safe deposit box.",
          "Use a new email address you log into only from your own phone or browser.",
          "If you’re monitored: do this work on a device you trust, like a friend’s laptop or a library computer.",
        ],
      },
      {
        body: "If you’re not ready to do any of it — that is also okay. Just reading this counts.",
      },
    ],
    sections_es: [
      {
        body: "La preparación económica no se trata de estar lista para irte. Se trata de darte opciones. Cada paso pequeño es una puerta más que se abre en silencio.",
      },
      {
        heading: "La lista de diez puntos",
        body: "Abre la tarjeta de Libertad Económica desde el hub de Santuario y toca cada punto según los vayas completando. Nada se califica. Nada se cronometra. El progreso es información, no presión.",
      },
      {
        heading: "Cómo usar la lista",
        list: [
          "Haz un punto esta semana. Solo uno.",
          "Guarda copias fuera de la casa — el cajón de una amiga, una nube que él no conozca, una caja de seguridad.",
          "Usa un correo nuevo al que solo entres desde tu propio teléfono o navegador.",
          "Si te monitorean: hazlo en un dispositivo de confianza, como la laptop de una amiga o una computadora de la biblioteca.",
        ],
      },
      {
        body: "Si todavía no estás lista para hacer nada — también está bien. Leer esto ya cuenta.",
      },
    ],
    reflection_en: "What is one tiny step I could take this week — even something nobody would notice?",
    reflection_es: "¿Cuál es un paso pequeñísimo que podría dar esta semana — incluso algo que nadie notaría?",
    related: [],
  },

  // ----------------------- HEALTHY RELATIONSHIPS ----------------------- //
  {
    slug: "green-vs-red",
    category: "healthy-relationships",
    read_minutes: 5,
    title_en: "Green Flags vs Red Flags",
    title_es: "Banderas Verdes vs. Banderas Rojas",
    sections_en: [
      {
        heading: "Accountability",
        body: "Green: apologizes without conditions, asks how the impact landed, changes the behavior. Red: apologizes with ‘but you,’ blames their mood / day / past, repeats the same behavior with a new explanation.",
      },
      {
        heading: "Respect",
        body: "Green: respects your no on the small things — drinks, plans, touch. Their respect doesn’t require persuasion. Red: respects your no only after a long explanation, or treats your no as a negotiation opener.",
      },
      {
        heading: "Consistency",
        body: "Green: the person you meet on month two is the same as month eight. Their attention is even, their effort steady. Boring is healing. Red: ‘love-bombing’ that fades, week-long cycles of warm/cold, big promises followed by no follow-through.",
      },
      {
        heading: "Healthy conflict",
        body: "Green: can stay in the same room when you disagree. Repairs within a day. Doesn’t score-keep. Red: stonewalls, escalates, brings up old wounds, makes you doubt your memory of what just happened.",
      },
      {
        heading: "One quiet test",
        body: "A safe person’s presence makes your nervous system slower. Your shoulders drop. Your jaw releases. You exhale all the way out. Use your body as data.",
      },
    ],
    sections_es: [
      {
        heading: "Rendición de cuentas",
        body: "Verde: pide perdón sin condiciones, pregunta cómo cayó el impacto, cambia la conducta. Rojo: pide perdón con ‘pero tú’, culpa a su ánimo / día / pasado, repite la misma conducta con una explicación nueva.",
      },
      {
        heading: "Respeto",
        body: "Verde: respeta tu no en las cosas pequeñas — bebidas, planes, contacto. Su respeto no requiere convencimiento. Rojo: respeta tu no solo después de una explicación larga, o trata tu no como el inicio de una negociación.",
      },
      {
        heading: "Consistencia",
        body: "Verde: la persona que conoces en el mes dos es la misma del mes ocho. Su atención es pareja, su esfuerzo constante. Lo aburrido también sana. Rojo: ‘amor-bombardeo’ que se apaga, ciclos semanales de calidez/frialdad, grandes promesas sin cumplimiento.",
      },
      {
        heading: "Conflicto sano",
        body: "Verde: puede quedarse en el mismo cuarto cuando no están de acuerdo. Repara dentro de un día. No lleva cuenta. Rojo: se cierra, escala, saca heridas viejas, te hace dudar de lo que acabas de recordar.",
      },
      {
        heading: "Una prueba silenciosa",
        body: "La presencia de una persona segura hace más lento tu sistema nervioso. Los hombros bajan. La mandíbula suelta. Exhalas hasta el final. Usa tu cuerpo como información.",
      },
    ],
    reflection_en: "What behaviors make me feel safe?",
    reflection_es: "¿Qué conductas me hacen sentir segura?",
    related: ["boundaries-101", "gray-rock"],
  },
];

export const CATEGORIES = [
  { slug: "all", label_en: "All", label_es: "Todas" },
  { slug: "boundaries", label_en: "Boundaries", label_es: "Límites" },
  { slug: "communication", label_en: "Communication", label_es: "Comunicación" },
  { slug: "financial-freedom", label_en: "Financial Freedom", label_es: "Libertad Económica" },
  { slug: "healthy-relationships", label_en: "Healthy Relationships", label_es: "Relaciones Sanas" },
];

export function listLessons() { return LESSONS; }
export function getLesson(slug) { return LESSONS.find((l) => l.slug === slug) || null; }

/** Daily lesson rotation — deterministic, mirrors paths.dailyPrompt. */
export function dailyLessonSlug(date = new Date()) {
  const start = new Date(date.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((date - start) / 86400000);
  return LESSONS[dayOfYear % LESSONS.length].slug;
}

export function categoryLabel(slug, lang = "en") {
  const c = CATEGORIES.find((x) => x.slug === slug);
  if (!c) return slug;
  return lang === "es" ? c.label_es : c.label_en;
}
